import { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import heic2any from 'heic2any'
import moment from 'moment'
import styled from '@emotion/styled'
import UploadIcon from 'assets/images/upload-img.png'
import ShareFileLogo from 'assets/images/share-file-logo.png'
import DragAndDropCard from './DragAndDropCard'
import {
  setAllCustomerFiles,
  setUpdatedDoc,
  setSingleDoc,
  setFilterFiles,
  setAllFilesLoader
} from 'slices/customerFiles'
import { setIdleTime } from 'slices/global'
import { useProcessAllFiles } from 'api'
import { generateUUID, collectDroppedFileNames } from 'utils/helpers'
import { useCustomer } from 'api/useCustomer'
import { api } from 'lib/axios'
import { getUserId } from 'lib/auth/tokenStorage'
import { useLogActivity } from 'api'

const UploadDocuments = ({
  customer,
  passport,
  DragDropTimeLog,
  onRetryUploadReady,
  setAlertModal
}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const fileInputRef = useRef(null)
  const customer_files = useSelector(state => state?.customer_files)
  const currentCustomerFiles = useSelector(state => state.customer_files?.file || state.customer_files?.docs)
  const { handleProcessAllFiles } = useProcessAllFiles()
  const { logBatchApiActivity } = useLogActivity()
  const formattedDate = moment().format('DD/MM/YYYY')
  const [preparingFiles, setPreparingFiles] = useState(false)
  const [, setDuplicateImagesFiles] = useState([])
  const [, setUploadFailed] = useState(false)
  const [, setUploadFailedText] = useState('')
  const [, setData] = useState({
    loading: false,
    error: '',
    message: ''
  })
  const { handleSyncDataDocs } = useCustomer()
  const [localFiles, setLocalFiles] = useState(new Map())
  const uploadStartTimeRef = useRef(null)
  const uploadEndTimeRef = useRef(null)
  const uploadCountRef = useRef(0)
  const completedUploadsRef = useRef(0)
  const retryingIdsRef = useRef(new Set())

  const prepareData = (array = [], docs = []) => {
    const arr = array.filter(item => {
      const { file_obj } = item
      const newArr = !docs.some(
        doc =>
          doc?.file_obj?.document_revision_id === file_obj?.document_revision_id
      )
      return newArr
    })
    return arr.map((item, idx) => {
      const { file_obj } = item

      return {
        file_no: idx,
        file: {},
        id: generateUUID(),
        fileName: file_obj?.name || '',
        status: 'Waiting for upload...',
        progress: 0,
        isUploaded: true,
        isError: false,
        file_obj: file_obj || {
          id: '',
          document_revision_id: '',
          gb_doc_type: null,
          name: '',
          rotate_angle_c: null
        }
      }
    })
  }

  const filterAllDocs = (files, docs) => {
    const docIds = new Set(docs.map(doc => doc.file_obj.id))
    return files.filter(file => !docIds.has(file.file_obj.id))
  }

  const trackActivity = async () => {
    try {
      const now = moment()
      const utcMoment = now.utc()
      const formattedTime = utcMoment.format('YYYY-MM-DD HH:mm:ss')

      let data = {
        data: {
          type: 'gb_action_tracking_logs',
          attributes: {
            affiliate_member_id: getUserId(),
            action: 'sharefile_sync',
            action_time: formattedTime,
            parent_id: customer.id
          }
        }
      }
      await api.post('/Api/V8/module', data)
    } catch (error) {
      console.log(error)
    }
  }

  const handleSyncData = async () => {
    setData({ loading: true, error: '' })
    dispatch(setAllFilesLoader({ loader: true }))
    try {
      const response = await handleSyncDataDocs(customer?.id)

      const { success, files, message } = response?.data
      if (success && files?.length > 0) {
        const prepareDocArr = prepareData(files, customer_files?.docs)
        const prepData = [
          ...(customer_files?.files?.length ? customer_files.files : []),
          ...(Array.isArray(prepareDocArr) ? prepareDocArr : [prepareDocArr])
        ]
        const uniquePrepData = Array.from(
          new Map(
            prepData.map(item => [item.file_obj.document_revision_id, item])
          ).values()
        )

        dispatch(
          setAllCustomerFiles({
            files: uniquePrepData
          })
        )

        const filterAllDocsArr = filterAllDocs(
          uniquePrepData,
          customer_files?.docs
        )
        filterAllDocsArr?.length > 0 &&
          filterAllDocsArr?.map(item =>
            dispatch(
              setSingleDoc({
                file: {
                  fileName: item?.file_name || '',
                  file_obj: {
                    ...item?.file_obj,
                    name: item?.file_obj?.name || item?.file_name
                  },
                  status: 'File uploaded successfully.',
                  progress: 100,
                  isUploaded: true,
                  isError: false
                }
              })
            )
          )
      }
      setData({
        loading: false,
        error: message || 'Error occurred...',
        message: message || ''
      })
      trackActivity()
    } catch (err) {
      const errorMessage = err.response
        ? err.response.data?.message || 'Unknown server error'
        : err.request
          ? 'No response from server'
          : err.message || 'Something went wrong'

      setData({ loading: false, error: errorMessage, message: '' })
    } finally {
      setData({ loading: false })
      dispatch(setAllFilesLoader({ loader: false }))
    }
  }

  const handleFileChange = async e => {
    if (!customer?.id) {
      console.error('Customer ID is required for file upload')
      return
    }

    setPreparingFiles(true)

    dispatch(
      setIdleTime({
        ideal_time_check: true,
        customerID: customer.id || ''
      })
    )

    if (passport?.flag !== true && DragDropTimeLog) {
      DragDropTimeLog('', 'upload_documents')
    }

    const selectedFiles = Array.from(e.target.files)

    const { allowedFiles, nonAllowedFiles } = await filterFiles(selectedFiles)

    setPreparingFiles(false)
    let duplicatesArr = []

    if (allowedFiles?.length > 0) {
      const { validFiles, duplicates, newLocalFiles } = prepareFilesArray(
        allowedFiles,
        currentCustomerFiles
      )
      duplicatesArr = [...duplicates]

      // Set local files first
      setLocalFiles(newLocalFiles)

      if (validFiles?.length > 0) {
        // Reset tracking for new batch
        uploadCountRef.current = validFiles.length
        completedUploadsRef.current = 0
        uploadStartTimeRef.current = null
        uploadEndTimeRef.current = null

        // Set start time before first upload
        uploadStartTimeRef.current = moment()

        for (let index = 0; index < validFiles.length; index++) {
          const fileId = validFiles[index].id
          const file = newLocalFiles.get(fileId)
          if (file) {
            const isLastFile = index === validFiles.length - 1
            await uploadFile({ id: fileId, file, isLastFile })
          }
        }
      }
    }

    if (nonAllowedFiles?.length > 0) {
      setAlertModal({
        open: true,
        message: `The following file(s) are not allowed: ${nonAllowedFiles
          .map(file => file.name)
          .join(', ')}. Please select different files.`
      })
      //setDisallowedImagesModal(true)
      // setDisAllowedFiles(nonAllowedFiles)
    }
    if (duplicatesArr?.length > 0) {
      //setDuplicateImagesModal(true)
      setAlertModal({
        open: true,
        message: `The following file(s) are already uploaded: ${duplicatesArr
          .map(dup => dup.fileName)
          .join(', ')}. Please select different files.`
      })
    }
    e.target.value = null
  }

  const retryUpload = async id => {
    if (!id || retryingIdsRef.current.has(id)) return
    const file = localFiles.get(id)
    if (!file) return

    retryingIdsRef.current.add(id)
    try {
      await uploadFile({ id, file })
    } finally {
      retryingIdsRef.current.delete(id)
    }
  }

  useEffect(() => {
    if (onRetryUploadReady) {
      onRetryUploadReady(() => retryUpload)
    }
  }, [onRetryUploadReady, localFiles])

  const pendingAuthRetryIds = useRef(new Set())

  const uploadFile = async (data, { _isAuthRetry = false } = {}) => {
    const { id, file: passedFile, isLastFile = false, isRetry = false } = data

    const file = passedFile || localFiles.get(id)
    const fileName = file?.name || ''

    try {
      const response = await handleProcessAllFiles(file, id, customer.id)

      pendingAuthRetryIds.current.delete(id)

      if (response?.type === 'doc_failure') {
        setUploadFailed(true)
        setUploadFailedText(response?.message || 'Unknown Error.')
        dispatch(
          setUpdatedDoc({
            id,
            data: {
              status: response?.message || 'Upload failed',
              isUploaded: false,
              isError: true
            }
          })
        )
        // Track completion even on failure (only for batch uploads, not retries)
        if (!isRetry) {
          completedUploadsRef.current += 1
          if (
            isLastFile ||
            completedUploadsRef.current === uploadCountRef.current
          ) {
            uploadEndTimeRef.current = moment()
            await logBatchActivityIfComplete()
          }
        }
        return
      }

      if (response?.length > 0) {
        const fileData = response[0]
        dispatch(
          setUpdatedDoc({
            id,
            data: {
              fileName: fileData?.file_name || '',
              file_obj: fileData,
              status: 'File uploaded successfully.',
              progress: 100,
              isUploaded: true,
              isError: false
            }
          })
        )
        dispatch(
          setSingleDoc({
            file: {
              fileName: fileData?.file_name || '',
              file_obj: {
                ...fileData,
                name: fileData?.file_name || ''
              },
              status: 'File uploaded successfully.',
              progress: 100,
              isUploaded: true,
              isError: false
            }
          })
        )
      } else {
        dispatch(
          setUpdatedDoc({
            id,
            data: {
              status: 'Upload error.',
              isUploaded: false,
              isError: true
            }
          })
        )
      }

      if (!isRetry) {
        completedUploadsRef.current += 1

        if (
          isLastFile ||
          completedUploadsRef.current === uploadCountRef.current
        ) {
          uploadEndTimeRef.current = moment()
          await logBatchActivityIfComplete()
        }
      }
    } catch (error) {
      const status = error?.response?.status
      const isAuthError = status === 401 || status === 403
      if (isAuthError && !_isAuthRetry) {
        const refreshedToken = localStorage.getItem('access_token')
        if (refreshedToken) {
          pendingAuthRetryIds.current.add(id)
          dispatch(
            setUpdatedDoc({
              id,
              data: {
                status: 'Session refreshed. Retrying upload...',
                progress: 0,
                isUploaded: true,
                isError: false
              }
            })
          )
          await uploadFile({ id, file }, { _isAuthRetry: true })
          return
        }
      }

      let errStatus = ''
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        errStatus = 'Network error. Please check your connection and retry.'
      } else if (error.message?.includes?.('ERR_CONNECTION_REFUSED')) {
        errStatus = 'Server unavailable, Connection refused'
      } else if (isAuthError) {
        errStatus = 'Session expired. Please log in again and retry the upload.'
      } else {
        errStatus = 'Uploading error.'
      }

      dispatch(
        setUpdatedDoc({
          id,
          data: {
            status: errStatus || 'Uploading error',
            isUploaded: false,
            isError: true
          }
        })
      )
      if (!isRetry) {
        completedUploadsRef.current += 1
        if (
          isLastFile ||
          completedUploadsRef.current === uploadCountRef.current
        ) {
          uploadEndTimeRef.current = moment()
          await logBatchActivityIfComplete()
        }
      }
    }
  }

  const logBatchActivityIfComplete = async () => {
    if (
      uploadStartTimeRef.current &&
      uploadEndTimeRef.current &&
      uploadCountRef.current > 0
    ) {
      const agentId = getUserId() || ''
      await logBatchApiActivity({
        contactId: customer.id,
        agentId,
        apiEndpoint: '/Api/V8/custom/customer/process-all-files',
        actionType: 'upload_documents',
        requestPayload: {
          contact_id: customer.id,
          total_files: uploadCountRef.current,
          uploaded_files: completedUploadsRef.current
        },
        startTime: uploadStartTimeRef.current.format('YYYY-MM-DD HH:mm:ss'),
        endTime: uploadEndTimeRef.current.format('YYYY-MM-DD HH:mm:ss')
      })
    }
  }

  const heifImageConverter = async heifImg => {
    let imageFile = heifImg
    try {
      const blob = await heic2any({
        blob: heifImg,
        toType: 'image/jpg'
      })
      imageFile = new File([blob], heifImg.name.replace(/\.[^/.]+$/, '.jpg'), {
        type: 'image/jpg'
      })
      return imageFile
    } catch (error) {
      return
    }
  }

  const filterFiles = async filesData => {
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/heif',
      'image/heic',
      'application/pdf'
    ]

    const allowedFiles = []
    const nonAllowedFiles = []

    for (const file of filesData) {
      if (allowedTypes.includes(file.type)) {
        if (file.type === 'image/heif' || file.type === 'image/heic') {
          const convertedFile = await heifImageConverter(file)
          allowedFiles.push(convertedFile)
        } else {
          allowedFiles.push(file)
        }
      } else {
        nonAllowedFiles.push(file)
      }
    }
    return {
      allowedFiles,
      nonAllowedFiles
    }
  }


  const prepareFilesArray = (array = [], currentCustomerFiles = []) => {
    // Normalize full filename so duplicates are checked including extension.
    const normalizeName = n =>
      typeof n === 'string' ? n.trim().toLowerCase() : ''
    const stripExt = n => n.replace(/\.[^.\\/]+$/, '')

    const collectNames = list => {
      const full = new Set()
      const base = new Set()
      for (const f of list || []) {
        const raw =
          f?.file_obj?.name ?? f?.file_obj?.file_name ?? f?.fileName ?? ''
        const norm = normalizeName(raw)
        if (!norm) continue
        full.add(norm)
        const b = stripExt(norm)
        if (b) base.add(b)
      }
      return { full, base }
    }


    const existing = collectNames(currentCustomerFiles)

    const reduxNames = collectNames(customer_files?.files)

    const droppedNamesRaw = (() => {
      try {
        return collectDroppedFileNames(store.getState())
      } catch (_) {
        return new Set()
      }
    })()
    const droppedFull = new Set()
    const droppedBase = new Set()
    droppedNamesRaw.forEach(raw => {
      const norm = normalizeName(raw)
      if (!norm) return
      droppedFull.add(norm)
      const b = stripExt(norm)
      if (b) droppedBase.add(b)
    })

    const allExistingNames = new Set([
      ...existing.full,
      ...reduxNames.full,
      ...droppedFull
    ])
    const allExistingBaseNames = new Set([
      ...existing.base,
      ...reduxNames.base,
      ...droppedBase
    ])

    const seenInBatch = new Set()
    const seenInBatchBase = new Set()

    const duplicates = []
    const validFiles = []
    const newLocalFiles = new Map(localFiles)
    const currentFileCount = currentCustomerFiles.length

    for (const item of array) {
      const name = item?.name ?? ''
      const normalized = normalizeName(name)
      const normalizedBase = stripExt(normalized)
      const id = generateUUID()
      const isDup =
        !normalized ||
        allExistingNames.has(normalized) ||
        seenInBatch.has(normalized) ||
        (!!normalizedBase &&
          (allExistingBaseNames.has(normalizedBase) ||
            seenInBatchBase.has(normalizedBase)))

      const obj = {
        file_no: currentFileCount + validFiles.length,
        id,
        fileName: name,
        status: 'Waiting for upload...',
        progress: 0,
        isUploaded: false,
        file_obj: {
          id: '',
          document_revision_id: '',
          gb_doc_type: null,
          name: '',
          rotate_angle_c: 0
        }
      }

      if (isDup) {
        duplicates.push(obj)
      } else {
        validFiles.push(obj)
        seenInBatch.add(normalized)
        if (normalizedBase) seenInBatchBase.add(normalizedBase)
        newLocalFiles.set(id, item)
      }
    }

    if (duplicates.length) setDuplicateImagesFiles(duplicates)
    if (validFiles.length) setLocalFiles(newLocalFiles)

    for (const file of validFiles) {
      dispatch(setFilterFiles({ file }))
    }

    return { validFiles, duplicates, newLocalFiles }
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }
  const checkFilesLoader = customer_files?.filesLoader

  return (
    <DragAndDropCard text="UPLOAD DOCUMENTS" height="100%" position="fixed">
      <UploadDocumentsContainer onClick={handleUploadClick}>
        <ShareFileContainer>
          <ShareFileBox>
            <SyncText>Click to Sync From</SyncText>
            <ShareFileLogoContainer
              onClick={e => {
                e.stopPropagation() // Prevent triggering the upload click
                if (!checkFilesLoader) handleSyncData()
              }}
            >
              <ShareFileImage src={ShareFileLogo} alt="ShareFile logo" />
              <ShareFileTitle>Share File</ShareFileTitle>
            </ShareFileLogoContainer>
            <LastSyncText>Last Sync: {formattedDate}</LastSyncText>
          </ShareFileBox>
          <SyncFileBorder />
        </ShareFileContainer>
        <UploadDocumentsRow>
          <UploadDocumentsIcon src={UploadIcon} alt="Upload Doc Icon" />
          <UploadDocumentsText>
            {preparingFiles
              ? 'Preparing files for upload...'
              : 'Click here to add documents from an external device'}
          </UploadDocumentsText>
          {preparingFiles && (
            <LoadingIndicator>
              <LoadingSpinner />
            </LoadingIndicator>
          )}
        </UploadDocumentsRow>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </UploadDocumentsContainer>
    </DragAndDropCard>
  )
}

const ShareFileContainer = styled.div`
  width: 100%;
  position: relative;
  text-align: center;
`

const ShareFileBox = styled.div`
  background: #ddd;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 0;
`

const ShareFileLogoContainer = styled.div`
  cursor: pointer;
  margin-top: 10px;
`

const ShareFileImage = styled.img`
  width: 35px;
`

const ShareFileTitle = styled.h3`
  color: #000;
  font-size: 15px;
  font-weight: bold;
  border: none;
  margin: 7px 0 0;
  text-transform: capitalize;
  padding-bottom: 0;
`

const SyncText = styled.p`
  border: none;
  padding: 0;
  color: black;
  margin: 0;
  font-size: 14px;
`

const LastSyncText = styled.p`
  font-size: 12px;
  padding: 8px 10px 0 10px;
  border: none;
  margin-bottom: 0;
  color: black;
`

const SyncFileBorder = styled.div`
  border-bottom: dashed 2px #dbdbdb;
  width: 75%;
  margin: -2px auto 0 auto;
`

const UploadDocumentsContainer = styled.div`
  border: dashed 2px #dbdbdb;
  height: 100vh;
  margin: 15px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: #999;
    background-color: #fafafa;
  }
`

const UploadDocumentsRow = styled.div`
  text-align: center;
  margin-top: 6rem;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

const UploadDocumentsIcon = styled.img`
  width: 70%;
  margin: auto;
`

const UploadDocumentsText = styled.p`
  font-size: 10px;
  text-align: center;
  padding: 10px 4px;
`

const LoadingIndicator = styled.div`
  margin-top: 10px;
`

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #000;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

export default UploadDocuments
